// Lead capture, posted straight to Supabase.
//
// No backend to host. Supabase exposes every table as a REST endpoint, so the
// website writes directly to the same Postgres the ERP already reads.
//
// The anon key is public and that is fine — it ships in the JavaScript by
// design. Row Level Security is what protects the data: anonymous visitors can
// INSERT a lead and nothing else, so nobody can read your customer list out of
// the browser. See supabase/leads.sql for the policies.
//
// Every call is fire-and-forget. A lead that fails to save must never block or
// break a customer's checkout, so failures are logged and swallowed.

import { SUPABASE_URL, SUPABASE_ANON_KEY, LEADS_TABLE } from '../data/supabase';

// Read from a config file rather than environment variables. Both end up in
// the same public bundle, but a config file cannot be silently dropped by a
// build setting. See src/data/supabase.js for why this is safe.
const URL_BASE = SUPABASE_URL;
const ANON_KEY = SUPABASE_ANON_KEY;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function attribution() {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source') || null,
    utm_medium: p.get('utm_medium') || null,
    utm_campaign: p.get('utm_campaign') || null,
    gclid: p.get('gclid') || null,
    referrer: (document.referrer || '').slice(0, 300) || null,
    user_agent: navigator.userAgent.slice(0, 300),
  };
}

function toRow({ ref, kind, quote, form = {}, pin }) {
  const address = [form.houseNo, form.street, form.landmark, pin?.city, pin?.state, form.pincode]
    .filter(Boolean).join(', ');

  return {
    ref,
    kind,
    name: form.fullName || null,
    mobile: form.mobile || null,
    email: form.email || null,
    house_no: form.houseNo || null,
    street: form.street || null,
    landmark: form.landmark || null,
    city: pin?.city || null,
    state: pin?.state || null,
    pincode: form.pincode || null,
    address: address || null,
    notes: form.notes || null,

    lines: (quote?.lines || []).map((l) => ({
      modelId: l.model.id,
      title: l.model.title,
      idu: l.model.variants[l.kit]?.idu || '',
      odu: l.model.variants[l.kit]?.odu || '',
      kit: l.kit,
      qty: l.qty,
      unitPrice: l.unitPrice,
      equipment: l.equipment,
      withInstall: l.withInstall,
      kitAddon: l.kitAddon,
      measurementsGiven: l.measurementsGiven,
      copperM: num(l.copperM),
      drainM: num(l.drainM),
      wireM: num(l.wireM),
      work: (l.work || []).map((w) => ({ key: w.key, label: w.label, amount: w.amount })),
    })),

    units: num(quote?.units),
    equipment: num(quote?.equipment),
    work_ex_gst: num(quote?.workExGst),
    work_gst: num(quote?.workGst),
    delivery_amount: num(quote?.deliveryAmount),
    total: num(quote?.total),
    savings: num(quote?.savings),
    delivery: quote?.delivery
      ? {
        zone: quote.delivery.zone || '',
        km: num(quote.delivery.km),
        dispatchFrom: quote.delivery.dispatchFrom || '',
        free: Boolean(quote.delivery.free),
        confidence: quote.delivery.confidence || '',
        trips: num(quote.delivery.trips),
      }
      : null,

    ...attribution(),
  };
}

// Emails the lead to admin@citicool.in through the Vercel serverless function
// at /api/notify-lead. Deliberately not awaited — the lead is already safe in
// Supabase, and a slow or failing mail server must never hold up the
// customer's confirmation screen.
function notify(args) {
  try {
    const { ref, kind, quote, form = {}, pin } = args;
    fetch('/api/notify-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        ref,
        kind,
        createdAt: new Date().toISOString(),
        name: form.fullName || '',
        mobile: form.mobile || '',
        email: form.email || '',
        address: [form.houseNo, form.street, form.landmark, pin?.city, pin?.state, form.pincode]
          .filter(Boolean).join(', '),
        notes: form.notes || '',
        utmSource: new URLSearchParams(window.location.search).get('utm_source') || '',
        utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
        lines: (quote?.lines || []).map((l) => ({
          title: l.model.title,
          idu: l.model.variants[l.kit]?.idu || '',
          kit: l.kit,
          qty: l.qty,
          withInstall: l.withInstall,
          kitAddon: l.kitAddon,
          measurementsGiven: l.measurementsGiven,
          copperM: num(l.copperM), drainM: num(l.drainM), wireM: num(l.wireM),
          work: (l.work || []).map((w) => ({ label: w.label, amount: w.amount })),
        })),
        totals: {
          equipment: num(quote?.equipment),
          workExGst: num(quote?.workExGst),
          workGst: num(quote?.workGst),
          deliveryAmount: num(quote?.deliveryAmount),
          total: num(quote?.total),
          savings: num(quote?.savings),
        },
        delivery: quote?.delivery || null,
      }),
    }).catch(() => { /* the lead is saved; the email is a bonus */ });
  } catch { /* never let notification break checkout */ }
}

// Which lead kinds email you the moment they are saved.
//
// Checkout steps do NOT. A customer who types their number and then pays used
// to send you three emails (abandoned, checkout started, paid) and two of them
// were wrong. Now the PAID email is the only instant one; anyone who started
// checkout and has not paid 5 minutes later gets ONE "not paid" alert from
// /api/unpaid-sweep, which Supabase runs every 5 minutes.
const INSTANT_ALERT = new Set(['request', 'download']);

// The request-a-unit form passes name, mobile and email at the top level
// rather than inside `form`. Without this they were saved blank.
function normalise(args) {
  if (args.form) return args;
  return {
    ...args,
    form: {
      fullName: args.name || '',
      mobile: args.mobile || '',
      email: args.email || '',
      pincode: args.pincode || '',
      notes: args.notes || '',
    },
  };
}

export async function saveLead(input) {
  const args = normalise(input);
  if (!URL_BASE || !ANON_KEY) {
    console.warn('Supabase not configured — check src/data/supabase.js', args.ref);
    return false;
  }

  try {
    // Header shape depends on the key format.
    //
    // Old-style anon keys are JWTs starting "eyJ" and go in BOTH apikey and
    // Authorization. New-style publishable keys (sb_publishable_...) are not
    // JWTs — putting one in the Authorization header makes the gateway try to
    // decode it as a token, and the request ends up under a role your RLS
    // policies were never written for. That surfaces as 42501 "violates row
    // level security" even though the policy looks correct.
    const isJwt = ANON_KEY.startsWith('eyJ');
    const headers = {
      'Content-Type': 'application/json',
      apikey: ANON_KEY,
      Prefer: 'return=minimal',
    };
    if (isJwt) headers.Authorization = `Bearer ${ANON_KEY}`;

    // Plain insert, no upsert.
    //
    // on_conflict=ref made Postgres run INSERT ... ON CONFLICT DO UPDATE, and
    // that path has to read the existing row to evaluate the update policy.
    // There is deliberately no SELECT policy for anonymous visitors — that is
    // what stops anyone pulling the customer list out with a public key — so
    // the whole statement was refused with 42501 before the insert policy was
    // ever considered.
    //
    // Deduplication does not need an upsert. The unique index on `ref` refuses
    // a repeat with 409, which is handled below as success, and the database
    // trigger already clears an abandoned row when its quote arrives.
    const res = await fetch(`${URL_BASE}/rest/v1/${LEADS_TABLE}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(toRow(args)),
      keepalive: true,   // survives the tab closing immediately after submit
    });

    // 409 means this reference is already recorded. Nothing to do, not a
    // failure — the abandoned capture fires repeatedly by design.
    if (res.status === 409) return true;

    if (!res.ok) {
      console.warn('lead rejected', res.status, await res.text());
      return false;
    }

    if (INSTANT_ALERT.has(args.kind)) notify(args);
    return true;
  } catch (err) {
    console.warn('lead not saved', err);
    return false;
  }
}